import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Loader2, Search, TrendingUp, TrendingDown, Sparkles } from 'lucide-react';
import { getSeoStats } from '@/lib/analytics-api';

interface Props {
  projectId: string | null;
  from: string;
  to: string;
  customerId?: string;
}

function fmt(n: number): string {
  return new Intl.NumberFormat('de-DE').format(n);
}
function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

export default function SeoPanel({ projectId, from, to, customerId }: Props) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['seo', projectId, from, to, customerId],
    queryFn: () => getSeoStats({ from, to, projectId: projectId! }, customerId),
    enabled: !!projectId,
  });

  // Trendfilter der Top-Suchanfragen: alle, nur steigende, nur fallende.
  const [trendFilter, setTrendFilter] = useState<'alle' | 'auf' | 'ab'>('alle');
  const sichtbar = useMemo(() => {
    const liste = data?.top_queries || [];
    if (trendFilter === 'alle') return liste;
    return liste.filter((q) => q.trend === trendFilter);
  }, [data, trendFilter]);
  const zaehler = useMemo(() => {
    const liste = data?.top_queries || [];
    return { auf: liste.filter((q) => q.trend === 'auf').length, ab: liste.filter((q) => q.trend === 'ab').length, neu: liste.filter((q) => q.trend === 'neu').length };
  }, [data]);

  if (!projectId) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 text-center text-sm text-gray-500">
        Bitte oben eine einzelne Landing Page auswählen, um die SEO-Daten (Google-Rankings) zu sehen.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 flex justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-red-200 p-4 text-sm text-red-600">
        SEO-Daten konnten nicht geladen werden: {(error as any).message}
      </div>
    );
  }

  if (!data?.configured) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
        <Search className="w-6 h-6 text-gray-300 mx-auto mb-2" />
        <p className="text-sm text-gray-500">
          Für diese Landing Page ist noch keine Google-Search-Console-Property hinterlegt.
        </p>
      </div>
    );
  }

  const chartData = (data.timeseries || []).map((d) => ({ ...d, label: formatDate(d.date) }));

  return (
    <div className="space-y-4">
      {/* SEO-Kennzahlen */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SeoCard label="Klicks" value={fmt(data.totals.clicks)} hint="Klicks aus der Google-Suche" />
        <SeoCard label="Impressionen" value={fmt(data.totals.impressions)} hint="Wie oft in den Suchergebnissen erschienen" />
        <SeoCard label="CTR" value={`${(data.totals.ctr * 100).toFixed(1)} %`} hint="Klickrate (Klicks ÷ Impressionen)" />
        <SeoCard
          label="Ø Position"
          value={data.totals.position > 0 ? data.totals.position.toFixed(1) : '–'}
          hint="Durchschnittliche Ranking-Position (kleiner = besser)"
        />
      </div>

      {/* Verlauf */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h2 className="text-sm font-semibold text-gray-900 mb-4">SEO-Verlauf</h2>
        {chartData.length === 0 ? (
          <div className="h-56 flex items-center justify-center text-sm text-gray-400">
            Noch keine Search-Console-Daten (Sync läuft nächtlich, Google-Daten hinken 1–3 Tage nach).
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} minTickGap={20} />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} allowDecimals={false} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line yAxisId="left" type="monotone" dataKey="clicks" name="Klicks" stroke="#3b82f6" strokeWidth={2} dot={false} />
              <Line yAxisId="right" type="monotone" dataKey="impressions" name="Impressionen" stroke="#06b6d4" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Top-Suchanfragen */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Top-Suchanfragen</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Über welche Google-Suchbegriffe die Seite gefunden wird. Pfeile: Vergleich mit dem Zeitraum davor
              {data.vorperiode ? ` (${formatDate(data.vorperiode.from)}–${formatDate(data.vorperiode.to)})` : ''}.
            </p>
          </div>
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 text-xs">
            {([['alle', 'Alle'], ['auf', `Steigt (${zaehler.auf})`], ['ab', `Fällt (${zaehler.ab})`]] as const).map(([key, label]) => (
              <button key={key} onClick={() => setTrendFilter(key)} className={`px-2.5 py-1 rounded-md font-medium ${trendFilter === key ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
        {sichtbar.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-gray-400">Keine Suchanfragen im Zeitraum.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs text-gray-400 uppercase tracking-wider">
                  <th className="px-4 py-2 font-medium w-8"></th>
                  <th className="px-4 py-2 font-medium">Suchbegriff</th>
                  <th className="px-4 py-2 font-medium text-right">Klicks</th>
                  <th className="px-4 py-2 font-medium text-right">Impress.</th>
                  <th className="px-4 py-2 font-medium text-right">Ø Pos.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {sichtbar.map((q) => {
                  const dImp = q.impressions_prev != null ? q.impressions - q.impressions_prev : null;
                  const dPos = q.position_prev != null ? q.position_prev - q.position : null; // positiv = besser
                  const dKl = q.clicks_prev != null ? q.clicks - q.clicks_prev : null;
                  const delta = (d: number | null, nachkomma = 0) =>
                    d == null ? null : (
                      <span className={`ml-1 text-[11px] ${d > 0 ? 'text-emerald-600' : d < 0 ? 'text-red-500' : 'text-gray-300'}`}>
                        {d > 0 ? '+' : ''}{nachkomma ? d.toFixed(nachkomma) : fmt(d)}
                      </span>
                    );
                  return (
                    <tr key={q.query} className="hover:bg-gray-50">
                      <td className="px-4 py-2">
                        {q.trend === 'auf' && <TrendingUp className="w-4 h-4 text-emerald-600" />}
                        {q.trend === 'ab' && <TrendingDown className="w-4 h-4 text-red-500" />}
                        {q.trend === 'neu' && <Sparkles className="w-4 h-4 text-blue-500" />}
                      </td>
                      <td className="px-4 py-2 text-gray-700 max-w-xs truncate" title={q.query}>{q.query}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{fmt(q.clicks)}{delta(dKl)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-gray-500">{fmt(q.impressions)}{delta(dImp)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-gray-500">{q.position.toFixed(1)}{delta(dPos, 1)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SeoCard({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4" title={hint}>
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</span>
      <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );
}
